
/* eslint-disable no-alert */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Select from 'react-select';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import LoadingContainer from '../components/LoadingContainer';
import SVGIcon from '../components/SVGIcon';
import Button from '../components/Button';
import PersonalDetailsInput from '../components/PersonalDetailsInput';
import PartnerInsightsReportEditor from '../components/PartnerInsightsReportEditor';
import PartnerInsightsReport from '../components/PartnerInsightsReport';

import {
  changePartnerAccountOwner,
  assignReportsToPartnerAccount,
  deleteLinkedPartnerAccount,
  createPartnerAccount,
} from '../store/actions/account-actions';

function compare(a, b) {
  const l1 = a.label.toUpperCase();
  const l2 = b.label.toUpperCase();

  let comparison = 0;
  if (l1 > l2) {
    comparison = 1;
  } else if (l1 < l2) {
    comparison = -1;
  }
  return comparison;
}

function listSubcategories(categories) {
  const list = [];

  categories.forEach(category => {
    category.subcategories.forEach(subcategory => list.push({
      label: subcategory.name,
      value: subcategory.name,
    }))
  });

  list.sort(compare);
  return list
}

function listOrganisations(categories) {
  const hmap = {};
  const list = [];

  categories.forEach(category => {
    category.subcategories.forEach(subcategory => {
      subcategory.organisations.forEach(organisation => {hmap[organisation.id] = organisation.name});
    });
  });

  for (const [value, label] of Object.entries(hmap)) {
    list.push({
      label,
      value
    })
  }

  list.sort(compare);
  return list;
}

const mapStateToProps = ({ client, account }) => ({
  clientName: client.name,
  loading: !account.partnerAccountsDataLoaded,
  partners:
    account.partnerAccountsDataLoaded && account.accountOverviewData.partners
      ? account.accountOverviewData.partners
      : [],
  subcategories:
    account.partnerAccountsDataLoaded &&
    account.accountOverviewData.client &&
    account.accountOverviewData.client.categories &&
    account.accountOverviewData.client.categories.length > 0
      ? listSubcategories(account.accountOverviewData.client.categories)
      : [],
  organisations:
    account.partnerAccountsDataLoaded &&
    account.accountOverviewData.client &&
    account.accountOverviewData.client.categories &&
    account.accountOverviewData.client.categories.length > 0
      ? listOrganisations(account.accountOverviewData.client.categories)
      : [],
  reports:
    account.partnerAccountsDataLoaded &&
    account.accountOverviewData.client &&
    account.accountOverviewData.client.reports
      ? account.accountOverviewData.client.reports
      : [],
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changePartnerAccountOwner,
      assignReportsToPartnerAccount,
      deleteLinkedPartnerAccount,
      createPartnerAccount,
    },
    dispatch
  );

class PartnerInsights extends React.Component {
  state = {
    modalOpen: false,
    modalTitle: '',
    modalBody: '',
    modalFooter: <React.Fragment />,
    editAccountOwnerRow: null,
    editReportsRow: null,
    addNewPartnerAccount: false,
    selectedAccountOwner: null,
    selectedReport: 0,
    editedReports: null,
    newClient: {
      clientName: '',
      ownerFirstName: '',
      ownerLastName: '',
      ownerEmail: '',
      reports: [],
    },
  };

  getOrganisationById = organisationId =>
    this.props.organisations.filter(
      organisation => organisation.value === organisationId
    )[0];

  getEditedReports = () =>
    this.state.editedReports.map(report => ({
      name: report.name,
      organisation: report.organisation.map(item => item.value),
      subcategories: report.subcategories.map(item => item.value),
    }));

  handleAddAccountButton = () => {
    this.setState(() => ({
      editAccountOwnerRow: null,
      editReportsRow: null,
      addNewPartnerAccount: true,
      editedReports: this.props.reports.map(report => ({
        name: report.name,
        organisation: [],
        subcategories: [],
      })),
      newClient: {
        clientName: '',
        ownerFirstName: '',
        ownerLastName: '',
        ownerEmail: '',
        reports: [],
      },
    }));
  };

  handleSaveNewPartnerAccountButton = () => {
    this.props.createPartnerAccount(
      {
        client: {
          name: this.state.newClient.clientName,
        },
        account: {
          username: this.state.newClient.ownerEmail,
          user: {
            firstName: this.state.newClient.ownerFirstName,
            lastName: this.state.newClient.ownerLastName,
          },
        },
      },
      this.getEditedReports()
    );
    this.handleCancelEditingButton();
  };

  handleEditAccountOwnerButton = index => {
    this.setState(() => ({
      editAccountOwnerRow: index,
      editReportsRow: null,
      addNewPartnerAccount: false,
      selectedAccountOwner: null,
    }));
  };

  handleEditReportPermissionsButton = index => {
    if (
      Array.isArray(this.props.partners[index].client.reports) &&
      this.props.partners[index].client.reports.length > 0
    ) {
      this.createReportsCopy(index);
    }
    this.setState(() => ({
      editAccountOwnerRow: null,
      editReportsRow: index,
      addNewPartnerAccount: false,
      selectedReport: 0,
    }));
  };

  handleCancelEditingButton = () => {
    this.setState(() => ({
      editAccountOwnerRow: null,
      editReportsRow: null,
      addNewPartnerAccount: false,
      modalOpen: false,
    }));
  };

  handleSelectAccountOwner = selectedAccountOwner =>
    this.setState(() => ({ selectedAccountOwner }));

  handleSaveAccountOwnerButton = index => {
    if (this.state.selectedAccountOwner !== null) {
      this.setState(prevState => ({
        modalOpen: true,
        modalTitle: 'Change Account Owner',
        modalBody: 'Are you sure you want to change the account owner?',
        modalFooter: (
          <React.Fragment>
            <Button color="secondary" onClick={this.closeModal}>
              Cancel
            </Button>{' '}
            <Button
              onClick={() => {
                this.handleCancelEditingButton();
                this.props.changePartnerAccountOwner(
                  this.props.partners[index].client.id,
                  prevState.selectedAccountOwner.id
                );
              }}
            >
              Change Owner
            </Button>
          </React.Fragment>
        ),
      }));
    }
  };

  handleSaveReportPermissionsButton = index => {
    this.handleCancelEditingButton();
    this.props.assignReportsToPartnerAccount(
      this.props.partners[index].client.id,
      this.getEditedReports()
    );
  };

  handleDeleteButton = index => {
    this.setState(() => ({
      modalOpen: true,
      modalTitle: 'Delete Partner Account',
      modalBody: `Are you sure you want to delete partner account ${
        this.props.partners[index].client.name
      }? This can not be undone.`,
      modalFooter: (
        <React.Fragment>
          <Button color="secondary" onClick={this.closeModal}>
            Cancel
          </Button>{' '}
          <Button
            onClick={() => {
              this.handleCancelEditingButton();
              this.props.deleteLinkedPartnerAccount(
                this.props.partners[index].client.id
              );
            }}
          >
            Delete Account
          </Button>
        </React.Fragment>
      ),
    }));
  };

  handleSelectReport = (report, selectedReport) => {
    this.setState(() => ({ selectedReport }));
  };

  handleSetReportOrganisations = organisation => {
    const { editedReports } = this.state;
    editedReports[this.state.selectedReport] = {
      ...editedReports[this.state.selectedReport],
      organisation,
    };
    this.setState(() => ({ editedReports }));
  };

  handleSetReportSubcategories = subcategories => {
    const { editedReports } = this.state;
    editedReports[this.state.selectedReport] = {
      ...editedReports[this.state.selectedReport],
      subcategories,
    };
    this.setState(() => ({ editedReports }));
  };

  createReportsCopy = partnerAccountIndex => {
    const partnerReports = this.props.partners[partnerAccountIndex].client
      .reports;
    const partnerReportNames = partnerReports.map(report => report.name);
    const editedReports = this.props.reports.map(report => {
      const partnerReportIndex = partnerReportNames.indexOf(report.name);
      return {
        ...report,
        organisation:
          partnerReportIndex > -1 &&
          Array.isArray(partnerReports[partnerReportIndex].organisation)
            ? partnerReports[partnerReportIndex].organisation.map(item => ({
                ...this.getOrganisationById(item),
              }))
            : [],
        subcategories:
          partnerReportIndex > -1 &&
          Array.isArray(partnerReports[partnerReportIndex].subcategories)
            ? partnerReports[partnerReportIndex].subcategories.map(item => ({
                label: item,
                value: item,
              }))
            : [],
      };
    });

    this.setState({ editedReports });
  };

  editNewClientProperty = (e, property) => {
    const newClient = { ...this.state.newClient };
    newClient[property] = e.target.value;
    this.setState({ newClient });
  };

  closeModal = () => this.setState(() => ({ modalOpen: false }));

  render() {
    return (
      <div className="PartnerInsights container">
        <div className="Account_section-title-row">
          <h3 className="Account_section-title-row_title">
            {this.props.clientName} - Partner Insights
          </h3>
          <Button
            onClick={this.handleAddAccountButton}
            color="link"
            label="Create Account"
          />
        </div>
        <LoadingContainer
          className="Account_personal-details content-card"
          loading={this.props.loading}
        >
          <table className="PartnerInsights_table">
            <colgroup>
              <col width="15%" />
              <col width="20%" />
              <col width="55%" />
              <col width="10%" />
            </colgroup>
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Account Owner</th>
                <th>Data Permissions</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {this.props.partners.map((partnerAccount, index) => {
                const accountOwner = partnerAccount.accounts
                  .filter(account => account.roles.indexOf('Owner') > -1)
                  .map(account => ({
                    firstName: account.user.firstName,
                    lastName: account.user.lastName,
                    username: account.username,
                  }))[0];
                return (
                  <tr key={partnerAccount.client.id}>
                    <td>
                      <p className="PartnerInsights_account-name">
                        {partnerAccount.client.name}
                      </p>
                    </td>
                    <td>
                      <div className="PartnerInsights_account-owner">
                        <span>
                          {accountOwner && accountOwner.firstName}{' '}
                          {accountOwner && accountOwner.lastName}
                        </span>
                        <button
                          type="button"
                          className="PartnerInsights_icon-button"
                          onClick={() =>
                            this.handleEditAccountOwnerButton(index)
                          }
                        >
                          <span className="screen-reader">edit</span>
                          <SVGIcon icon="edit" />
                        </button>
                      </div>
                      <p>{accountOwner && accountOwner.username}</p>
                      {index === this.state.editAccountOwnerRow && (
                        <React.Fragment>
                          <label
                            className="PartnerInsights_account-owner-select-label"
                            htmlFor="PartnerInsights-select-account-owner"
                          >
                            Select New Owner
                          </label>
                          <Select
                            id="PartnerInsights-select-account-owner"
                            value={this.state.selectedAccountOwner}
                            onChange={this.handleSelectAccountOwner}
                            options={partnerAccount.accounts.map(account => ({
                              ...account,
                              label: `${account.user.firstName} ${
                                account.user.lastName
                              }`,
                              value: account.id,
                            }))}
                          />
                          <div className="PartnerInsights_button_wrapper">
                            <Button
                              color="secondary"
                              label="Cancel"
                              onClick={this.handleCancelEditingButton}
                            />{' '}
                            <Button
                              label="Save"
                              onClick={() =>
                                this.handleSaveAccountOwnerButton(index)
                              }
                            />
                          </div>
                        </React.Fragment>
                      )}
                    </td>
                    <td>
                      {this.state.editReportsRow === index ? (
                        <PartnerInsightsReportEditor
                          reports={this.state.editedReports}
                          organisations={this.props.organisations}
                          subcategories={this.props.subcategories}
                          index={index}
                          selectedReport={this.state.selectedReport}
                          editedReport={
                            this.state.editedReports[this.state.selectedReport]
                          }
                          handleSelectReport={this.handleSelectReport}
                          handleSetReportOrganisations={
                            this.handleSetReportOrganisations
                          }
                          handleSetReportSubcategories={
                            this.handleSetReportSubcategories
                          }
                          handleCancelEditingButton={
                            this.handleCancelEditingButton
                          }
                          handleSaveReportPermissionsButton={
                            this.handleSaveReportPermissionsButton
                          }
                        />
                      ) : (
                        <ul className="PartnerInsights_report-list-ul">
                          {partnerAccount.client.reports.map(report => (
                            <PartnerInsightsReport
                              key={report.name}
                              name={report.name}
                              organisation={report.organisation}
                              subcategories={report.subcategories}
                            />
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>
                      {partnerAccount.client.reports.length > 0 && (
                        <button
                          type="button"
                          className="PartnerInsights_icon-button"
                          onClick={() =>
                            this.handleEditReportPermissionsButton(index)
                          }
                        >
                          <span className="screen-reader">edit</span>
                          <SVGIcon icon="edit" />
                        </button>
                      )}
                      <button
                        type="button"
                        className="PartnerInsights_icon-button"
                        onClick={() => this.handleDeleteButton(index)}
                      >
                        <span className="screen-reader">delete</span>
                        <SVGIcon icon="delete" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {this.state.addNewPartnerAccount && (
                <tr>
                  <td>
                    <PersonalDetailsInput
                      id="new-partner-account-client-name"
                      label="Client Name"
                      editing
                      onChange={e =>
                        this.editNewClientProperty(e, 'clientName')
                      }
                      value={this.state.newClient.clientName}
                    />
                  </td>
                  <td>
                    <PersonalDetailsInput
                      id="new-partner-account-owner-first-name"
                      label="First Name"
                      editing
                      onChange={e =>
                        this.editNewClientProperty(e, 'ownerFirstName')
                      }
                      value={this.state.newClient.ownerFirstName}
                    />
                    <PersonalDetailsInput
                      id="new-partner-account-owner-last-name"
                      label="Last Name"
                      editing
                      onChange={e =>
                        this.editNewClientProperty(e, 'ownerLastName')
                      }
                      value={this.state.newClient.ownerLastName}
                    />
                    <PersonalDetailsInput
                      id="new-partner-account-owner-email"
                      label="Email"
                      inputType="email"
                      editing
                      onChange={e =>
                        this.editNewClientProperty(e, 'ownerEmail')
                      }
                      value={this.state.newClient.ownerEmail}
                    />
                  </td>
                  <td>
                    <PartnerInsightsReportEditor
                      reports={this.props.reports}
                      organisations={this.props.organisations}
                      subcategories={this.props.subcategories}
                      index={-1}
                      selectedReport={this.state.selectedReport}
                      editedReport={
                        this.state.editedReports[this.state.selectedReport]
                      }
                      handleSelectReport={this.handleSelectReport}
                      handleSetReportOrganisations={
                        this.handleSetReportOrganisations
                      }
                      handleSetReportSubcategories={
                        this.handleSetReportSubcategories
                      }
                      handleCancelEditingButton={this.handleCancelEditingButton}
                      handleSaveReportPermissionsButton={
                        this.handleSaveNewPartnerAccountButton
                      }
                    />
                  </td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </LoadingContainer>
        <Modal isOpen={this.state.modalOpen} toggle={this.closeModal} centered>
          <ModalHeader toggle={this.closeModal}>
            {this.state.modalTitle}
          </ModalHeader>
          {this.state.modalBody && (
            <ModalBody>{this.state.modalBody}</ModalBody>
          )}
          {this.state.modalFooter && (
            <ModalFooter>{this.state.modalFooter}</ModalFooter>
          )}
        </Modal>
      </div>
    );
  }
}

PartnerInsights.propTypes = {
  clientName: PropTypes.string.isRequired,
  partners: PropTypes.arrayOf(PropTypes.object).isRequired,
  organisations: PropTypes.arrayOf(PropTypes.object).isRequired,
  subcategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  reports: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  changePartnerAccountOwner: PropTypes.func.isRequired,
  assignReportsToPartnerAccount: PropTypes.func.isRequired,
  deleteLinkedPartnerAccount: PropTypes.func.isRequired,
  createPartnerAccount: PropTypes.func.isRequired,
};

PartnerInsights.defaultProps = {
  loading: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PartnerInsights);
